<template>
    <div
        @click="openSearch"
        class="antialiased md:min-w-72 bg-white cursor-pointer rounded-full md:border border-border text-text-secondary/80 hover:opacity-60 py-2.5 md:px-6 flex gap-x-8 items-end ring-opacity-0 hover:ring-opacity-100 transition duration-200 focus-style font-semibold"
    >
        <div class="flex gap-1">
            <PhMagnifyingGlass class="w-6 stroke-current" />
            <p
                class="hidden md:inline text-sm pt-1"
                v-text="$lang.get('Search for...')"
            />
        </div>
        <div
            class="hidden md:flex h-6 items-center font-semibold text-sm flex-shrink-0 ml-auto"
        >
            <p
                :class="[
                    { 'text-2xl font-bold leading-6 px-1.5': isMac },
                    'font-mono border rounded px-1',
                ]"
                v-text="isMac ? '⌘' : 'Ctrl'"
            />
            <p class="ml-1">+ K</p>
        </div>
    </div>

    <TransitionRoot appear :show="searchOpen" as="template">
        <Dialog as="div" @close="closeSearch" class="relative z-modal">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-slate-900/25 backdrop-blur" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto flex justify-center">
                <div class="mx-auto w-full max-w-2xl p-4 pt-24 sm:pt-16">
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel
                            class="overflow-hidden rounded-lg border border-border bg-white shadow-md transition-all"
                        >
                            <Popover as="div" class="relative w-full">
                                <div class="relative">
                                    <div
                                        class="absolute left-0 top-0 flex items-center pl-4 h-full"
                                    >
                                        <PhMagnifyingGlass
                                            class="h-6 stroke-slate-400"
                                        />
                                    </div>
                                    <input
                                        v-model="query"
                                        :placeholder="
                                            $lang.get('Search for...')
                                        "
                                        type="text"
                                        :class="[
                                            { 'shadow-sm': query },
                                            ' block w-full appearance-none bg-transparent py-4 pl-12 pr-[4.5rem]  text-base text-slate-900 placeholder:text-slate-600 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6 border-0',
                                        ]"
                                    />
                                    <div
                                        class="absolute right-0 top-0 flex items-center pr-4 h-full"
                                    >
                                        <PhCircleNotch
                                            v-if="searching"
                                            class="w-5 mr-1.5 animate-spin fill-text-secondary"
                                        />

                                        <PrimaryButton
                                            tabindex="-1"
                                            @click="closeSearch"
                                            class="md:hidden w-8 h-8 group hover:shadow-inner hover:bg-accent-light/30 inline-flex justify-center rounded-full"
                                        >
                                            <PhX
                                                class="w-6 h-6 flex-shrink-0 group-hover:scale-90 transition-transform duration-button-hover fill-current"
                                            />
                                        </PrimaryButton>

                                        <button
                                            tabindex="-1"
                                            @click="closeSearch"
                                            class="hidden md:flex text-xxs font-bold font-mono border border-gray-200 hover:border-gray-400/70 rounded-md py-0.5 md:py-[4px] px-1 md:px-[5px] shadow-sm transition-colors duration-button-hover"
                                        >
                                            <p
                                                class="hidden md:flex"
                                                v-text="'ESC'"
                                            />
                                        </button>
                                    </div>
                                </div>
                                <ReactiveHeightTransition>
                                    <PopoverPanel
                                        static
                                        v-if="showResultsPanel"
                                        as="div"
                                        class="border-t border-border"
                                    >
                                        <div
                                            class="px-4 pt-2 pb-4 space-y-2 border-b border-border"
                                        >
                                            <p
                                                class="font-semibold font-header"
                                                v-text="
                                                    $lang.get('Filter by type')
                                                "
                                            />
                                            <div class="flex flex-wrap gap-2">
                                                <!-- Disable the last selected model class, have to have at least one selected -->
                                                <ModelClassTag
                                                    v-for="modelClass in $getAvailableModels()"
                                                    :key="modelClass"
                                                    :modelClass="modelClass"
                                                    :clickable="true"
                                                    @click="
                                                        toggleModelClass(
                                                            modelClass
                                                        )
                                                    "
                                                    :faded="
                                                        !selectedModelClasses.includes(
                                                            modelClass
                                                        )
                                                    "
                                                />
                                            </div>
                                        </div>
                                        <div class="max-h-[60vh] overflow-auto">
                                            <div
                                                v-if="
                                                    results &&
                                                    results.length == 0 &&
                                                    query.length > 2
                                                "
                                                class="text-text bg-white w-full border border-t-0 h-full flex flex-col items-center justify-center text-center px-4 py-8"
                                            >
                                                <p
                                                    class="font-bold"
                                                    v-text="
                                                        $lang.get(
                                                            `Couldn't find and mathing items`
                                                        )
                                                    "
                                                />
                                                <p
                                                    class="text-slate-500 mt-2"
                                                    v-text="
                                                        $lang.get(
                                                            `We can't find anything that matches your search term at the moment, please try searching something else.`
                                                        )
                                                    "
                                                />
                                            </div>
                                            <div
                                                v-if="
                                                    results &&
                                                    results.length > 0
                                                "
                                                class="flex flex-col divide-y-[0.5px]"
                                            >
                                                <SearchResult
                                                    v-for="res in results"
                                                    :key="`${res.id}-${res.modelClass}`"
                                                    :result="res"
                                                    @click="closeSearch"
                                                />
                                            </div>
                                        </div>
                                    </PopoverPanel>
                                </ReactiveHeightTransition>
                            </Popover>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import { PhMagnifyingGlass, PhX, PhCircleNotch } from '@phosphor-icons/vue'
import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
} from '@headlessui/vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import { throttle, debounce } from '@/Tools/utilities.js'
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import SearchResult from '@/Components/Search/SearchResult.vue'
import ModelClassTag from '@/Components/ModelComponents/ModelClassTag.vue'

export default {
    name: 'GlobalSearchBar',
    components: {
        Link,
        PhMagnifyingGlass,
        PhX,
        PhCircleNotch,
        TransitionRoot,
        TransitionChild,
        Dialog,
        DialogPanel,
        DialogTitle,
        ReactiveHeightTransition,
        Popover,
        PopoverButton,
        PopoverPanel,
        PrimaryButton,
        SearchResult,
        ModelClassTag,
    },

    data: () => ({
        searchOpen: false,
        query: '',
        results: null,
        searching: false,
        selectedModelClasses: [],
    }),

    created() {
        document.addEventListener('keydown', this.handleKeyDown)

        this.throttledSearch = throttle(() => {
            this.search()
        }, 300)

        this.debouncedSearch = debounce(() => {
            this.search()
        }, 200)

        this.selectedModelClasses = [...this.$getAvailableModels()]
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown)
    },

    computed: {
        showResultsPanel() {
            return this.query.length > 2 && Array.isArray(this.results)
        },

        isMac() {
            return navigator.userAgent.includes('Mac')
        },
    },

    watch: {
        query() {
            if (this.query && this.query.length >= 3) {
                this.throttledSearch()
                this.debouncedSearch()
            }
        },

        selectedModelClasses() {
            if (this.query && this.query.length >= 3) {
                this.debouncedSearch()
            }
        },
    },

    methods: {
        search() {
            this.searching = true
            axios
                .get(route('api.basemodels.search'), {
                    params: {
                        query: this.query,
                        model_classes: this.selectedModelClasses,
                        is_global_search: 1,
                    },
                })
                .then(({ data }) => {
                    this.results = data
                    this.searching = false
                })
                .catch((error) => {
                    console.error(error)
                })
        },

        handleKeyDown(event) {
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                this.openSearch()
            } else if (event.key === 'Escape' && this.searchOpen) {
                this.closeSearch()
            }
        },

        openSearch() {
            this.searchOpen = true
        },

        closeSearch() {
            this.searchOpen = false
            this.query = ''
            this.results = null
        },

        /**
         * When pressing a modelClass tag, it will be the only one selected
         * But if it is already the only selected, then we add all the other ones again
         */
        toggleModelClass(modelClass) {
            if (
                this.selectedModelClasses.includes(modelClass) &&
                this.selectedModelClasses.length == 1
            ) {
                this.selectedModelClasses = this.$getAvailableModels()
            } else {
                this.selectedModelClasses = [modelClass]
            }
        },
    },
}
</script>
